import { useRef, useState } from "react";
import { uploadImage, type ImageUploadResult } from "@/utils/uploadUtils";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";

interface OnboardingPhotoStepProps {
  photoUri: string | null;
  setPhotoUri: (uri: string | null) => void;
  uploadResult: ImageUploadResult | null;
  setUploadResult: (result: ImageUploadResult | null) => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
  userId: string;
  onContinue: () => void;
}

export function OnboardingPhotoStep({
  photoUri,
  setPhotoUri,
  uploadResult,
  setUploadResult,
  isUploading,
  setIsUploading,
  userId,
  onContinue,
}: OnboardingPhotoStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [updateProfile, { isLoading: isSaving }] =
    useUpdateUserProfileMutation();

  const handleSelectImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPhotoUri(previewUrl);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });
      setUploadResult(result);
    } catch (error) {
      console.error("[OnboardingPhoto] Upload failed:", error);
      window.alert(
        "Failed to upload photo. You can try again or continue without a photo."
      );
    } finally {
      setIsUploading(false);
    }

    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const handleContinue = async () => {
    if (photoUri && isUploading) {
      window.alert("Photo is still uploading. Please wait a moment.");
      return;
    }

    if (photoUri && uploadResult && userId) {
      try {
        await updateProfile({
          id: userId,
          avatar_small: uploadResult.imageSmall,
          avatar_medium: uploadResult.imageMedium,
          avatar_full: uploadResult.imageFull,
        }).unwrap();
      } catch (error) {
        console.error("[OnboardingPhoto] Save failed:", error);
        window.alert("Failed to save photo. Please try again.");
        return;
      }
    }

    onContinue();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto autohide-scrollbar px-8 pt-4 pb-8">
        <div className="flex flex-col gap-6">
          <h1 className="text-h1 text-text-primary">Add your photo</h1>

          {/* Photo area */}
          {photoUri ? (
            <div className="relative w-full aspect-square rounded-3xl overflow-hidden border border-border-tertiary">
              <img
                src={photoUri}
                alt="Profile photo"
                className="w-full h-full object-cover"
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="text-white text-body-1-medium">
                    {Math.round(uploadProgress * 100)}%
                  </div>
                </div>
              )}
              <button
                onClick={handleSelectImage}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm text-body-2-medium text-text-primary hover:bg-white/90 transition-colors"
              >
                Change your photo
              </button>
            </div>
          ) : (
            <button
              onClick={handleSelectImage}
              className="w-full aspect-square rounded-3xl border border-dashed border-border-tertiary bg-surface flex flex-col items-center justify-center gap-4 hover:border-border-secondary transition-colors"
            >
              <Icon
                name="camera"
                size={48}
                className="text-text-tertiary"
              />
              <span className="px-6 py-2.5 rounded-full bg-surface-2 text-body-2-medium text-text-secondary">
                Add a profile photo
              </span>
            </button>
          )}

          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface">
            <Icon name="eyes" size={16} className="text-text-secondary mt-0.5 flex-shrink-0" />
            <p className="text-body-2 text-text-secondary">
              You're more likely to be invited to clubs and events when you
              add a photo. You can change it anytime — no pressure.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-8 pb-6 pt-4">
        <button
          onClick={handleContinue}
          disabled={isSaving || isUploading}
          className={cn(
            "w-full py-3.5 rounded-full text-body-1-medium font-medium transition-opacity",
            !isSaving && !isUploading
              ? "bg-text-primary text-text-reversed hover:opacity-90"
              : "bg-border-tertiary text-text-tertiary cursor-not-allowed"
          )}
        >
          {isUploading ? "Uploading..." : "Continue"}
        </button>
      </div>
    </div>
  );
}
